export const casePrepBuddyGlossary = {
  address: {
    key: 'address',
    term: 'address',
    shortDefinition: 'An on-chain identifier that can send, receive, or hold digital assets.',
    longDefinition:
      'An address is a destination or source on a blockchain. Different blockchains use different address formats, and one person or service may control many addresses.',
    example: 'Examples include wallet addresses, exchange deposit addresses, and withdrawal addresses.'
  },

  addressHash: {
    key: 'addressHash',
    term: 'address hash',
    shortDefinition: 'The text form of an address, usually copied and pasted as an identifier.',
    longDefinition:
      'People often say "address hash" when referring to the visible string value of an address. In everyday use, this usually just means the address text itself.',
    example: 'Example: 0x1234abcd5678ef90...'
  },

  transaction: {
    key: 'transaction',
    term: 'transaction',
    shortDefinition: 'A recorded on-chain action such as sending funds, receiving funds, or interacting with a contract.',
    longDefinition:
      'A transaction is a single event written to a blockchain. Depending on the network, it may represent a transfer, a token movement, a swap, an approval, or another contract interaction.',
    example: 'Examples include deposits, withdrawals, swaps, approvals, and transfers.'
  },

  transactionHash: {
    key: 'transactionHash',
    term: 'transaction hash',
    shortDefinition: 'The unique identifier assigned to a specific transaction.',
    longDefinition:
      'A transaction hash is the reference value used to locate one exact transaction on a blockchain. It is commonly used to search for transaction details in block explorers and analysis tools.',
    example: 'Example: 0x followed by 64 hexadecimal characters on many EVM networks.'
  },

  toAddress: {
    key: 'toAddress',
    term: 'To Address',
    shortDefinition: 'The destination address a transaction was sent to.',
    longDefinition:
      'The To Address is the receiving address or target address for a transaction. In some cases, this field is especially important for understanding where funds were directed.',
    example: 'Example: a transfer sent from one wallet address to another wallet or deposit address.'
  },

  fromAddress: {
    key: 'fromAddress',
    term: 'From Address',
    shortDefinition: 'The address that initiated or sent the transaction.',
    longDefinition:
      'The From Address is the sending or initiating address associated with a transaction. It is often the starting point when tracing where funds moved next.',
    example: 'Example: a wallet address that sent ETH, USDT, or another token.'
  },

  blockchain: {
    key: 'blockchain',
    term: 'blockchain',
    shortDefinition: 'A shared digital ledger that records transactions in an ordered and verifiable way.',
    longDefinition:
      'A blockchain is a system for recording data across many computers so that the history of transactions is transparent and difficult to alter retroactively. Different blockchains may have different rules, speeds, costs, and address formats.',
    example: 'Examples include Bitcoin, Ethereum, Tron, Solana, and XRP Ledger.'
  },

  network: {
    key: 'network',
    term: 'network',
    shortDefinition: 'A specific blockchain environment where addresses, transactions, and assets exist.',
    longDefinition:
      'A network is the particular blockchain or chain context a transaction belongs to. The same-looking address or token symbol can mean different things on different networks.',
    example: 'Examples include Ethereum Mainnet, BNB Smart Chain, Arbitrum, Tron, and Base.'
  },

  wallet: {
    key: 'wallet',
    term: 'wallet',
    shortDefinition: 'A tool or application used to manage blockchain addresses and sign transactions.',
    longDefinition:
      'A wallet usually stores or controls the keys needed to access funds and approve blockchain activity. A wallet may contain one or many addresses, depending on the blockchain and wallet design.',
    example: 'Examples include MetaMask, Trust Wallet, Ledger, and exchange-hosted wallets.'
  },

  token: {
    key: 'token',
    term: 'token',
    shortDefinition: 'A digital asset that exists on a blockchain, often under a smart contract.',
    longDefinition:
      'A token is a blockchain-based asset that can represent currency, utility, ownership, or another type of value. Many tokens exist on top of larger networks rather than operating as separate blockchains.',
    example: 'Examples include USDT, USDC, LINK, and many ERC-20 or TRC-20 assets.'
  },

  nativeAsset: {
    key: 'nativeAsset',
    term: 'native asset',
    shortDefinition: 'The built-in asset of a blockchain network, used directly by that network.',
    longDefinition:
      'A native asset is the main asset that belongs to a blockchain itself rather than being issued by a token contract. Native assets are often used to pay transaction fees.',
    example: 'Examples include BTC on Bitcoin, ETH on Ethereum, TRX on Tron, and SOL on Solana.'
  },

  smartContract: {
    key: 'smartContract',
    term: 'smart contract',
    shortDefinition: 'Code on a blockchain that can hold assets and execute predefined logic.',
    longDefinition:
      'A smart contract is a program deployed on-chain. It can receive transactions, manage tokens, enforce rules, and trigger actions automatically when its conditions are met.',
    example: 'Examples include token contracts, decentralized exchange contracts, and staking contracts.'
  },

  blockExplorer: {
    key: 'blockExplorer',
    term: 'block explorer',
    shortDefinition: 'A website or tool used to look up blockchain transactions, addresses, and blocks.',
    longDefinition:
      'A block explorer lets people inspect public blockchain data. It is commonly used to view transaction histories, balances, token transfers, and contract interactions.',
    example: 'Examples include Etherscan, Tronscan, Blockchain.com Explorer, and Solscan.'
  },

  exchange: {
    key: 'exchange',
    term: 'exchange',
    shortDefinition: 'A service where users can buy, sell, swap, deposit, or withdraw digital assets.',
    longDefinition:
      'An exchange may be centralized or decentralized. In investigations, exchange-related addresses often appear as deposit addresses, hot wallets, or service addresses.',
    example: 'Examples include Binance, Coinbase, Kraken, and decentralized exchanges such as Uniswap.'
  },

  depositAddress: {
    key: 'depositAddress',
    term: 'deposit address',
    shortDefinition: 'An address provided so funds can be sent into a wallet, service, or exchange account.',
    longDefinition:
      'A deposit address is meant to receive incoming funds. On some services, a deposit address may be unique to one account, while on others it may be reused or managed as part of a larger system.',
    example: 'Example: an exchange gives a user a BTC or ETH deposit address so they can transfer funds in.'
  },

  withdrawal: {
    key: 'withdrawal',
    term: 'withdrawal',
    shortDefinition: 'A movement of funds out from a wallet, service, or account to another address.',
    longDefinition:
      'A withdrawal is an outgoing transfer. In many scenarios, it refers to funds leaving an exchange, custodial platform, or wallet and moving to another blockchain address.',
    example: 'Example: funds leaving an exchange account and arriving at a personal wallet address.'
  },

  transfer: {
    key: 'transfer',
    term: 'transfer',
    shortDefinition: 'A movement of an asset from one address to another.',
    longDefinition:
      'A transfer usually refers to funds or tokens changing addresses. Depending on the blockchain and asset type, the transfer may appear directly in the transaction or in related token transfer records.',
    example: 'Example: sending USDT from one address to another.'
  },

  gasFee: {
    key: 'gasFee',
    term: 'gas fee',
    shortDefinition: 'The fee paid to process a transaction on a blockchain.',
    longDefinition:
      'A gas fee is the cost of having a transaction executed and recorded by the network. The exact fee model depends on the blockchain.',
    example: 'Example: paying ETH to send an ERC-20 token transaction on Ethereum.'
  },

  blockchainAnalysis: {
    key: 'blockchainAnalysis',
    term: 'blockchain analysis',
    shortDefinition: 'The process of examining on-chain activity to understand movement, relationships, and behavior.',
    longDefinition:
      'Blockchain analysis involves reviewing addresses, transactions, timing, assets, and counterparties to understand what happened on-chain and how funds moved.',
    example: 'Examples include tracing stolen funds, reviewing exchange exposure, and following transfers forward through multiple hops.'
  }
};

export function getCasePrepBuddyGlossaryEntry(key) {
  return casePrepBuddyGlossary[key] || null;
}

export function listCasePrepBuddyGlossaryEntries() {
  return Object.values(casePrepBuddyGlossary);
}